"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/components/ui/cn";
import { usePageOverlay } from "@/hooks/usePageOverlay";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Footer area, typically action buttons. */
  footer?: React.ReactNode;
  className?: string;
  /**
   * On phones (`< sm`) the dialog fills the whole screen instead of floating
   * as a card — the working pattern for the warehouse operators (see the
   * project CLAUDE.md: overlays go full-screen rather than floating).
   */
  fullScreenOnMobile?: boolean;
  children: React.ReactNode;
}

/**
 * Body-scroll lock shared by every open Modal. A plain save/restore breaks
 * when modals nest (a ConfirmDialog inside a form Modal): the inner one
 * restores `overflow` to the "hidden" the outer one had set, leaving the page
 * unscrollable after both close. Counting open modals and only touching
 * `document.body` on the first lock / last unlock avoids that.
 */
let lockCount = 0;
let lockedPrevOverflow = "";
function lockBodyScroll() {
  if (lockCount === 0) {
    lockedPrevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  lockCount += 1;
}
function unlockBodyScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.style.overflow = lockedPrevOverflow;
  }
}

/**
 * Accessible-ish modal dialog rendered through a portal. Closes on overlay
 * click and Escape. Body scroll is locked while open.
 */
export function Modal({
  open,
  onClose,
  title,
  footer,
  className,
  fullScreenOnMobile,
  children,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    lockBodyScroll();
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockBodyScroll();
    };
  }, [open, onClose]);

  // `absolute`, not `fixed`: see usePageOverlay (iOS toolbar tint).
  const overlayRef = useRef<HTMLDivElement>(null);
  // A full-screen-on-phones dialog continues as the dialog surface under
  // Safari's toolbar; a floating one, as the scrim.
  usePageOverlay(overlayRef, open, { bleed: fullScreenOnMobile ? "var(--surface)" : undefined });

  if (!open || typeof document === "undefined") return null;

  // Let a caller-supplied `max-w-*` win over the default without needing
  // tailwind-merge (cn is a plain concat, so both would otherwise ship and
  // CSS order would decide).
  const overridesMaxWidth = /(?:^|\s)(?:sm:)?max-w-/.test(className ?? "");
  const defaultMaxWidth = overridesMaxWidth
    ? ""
    : fullScreenOnMobile
      ? "sm:max-w-md"
      : "max-w-md";

  return createPortal(
    <div
      ref={overlayRef}
      className={cn(
        "absolute inset-x-0 top-0 z-50 flex h-dvh items-center justify-center bg-scrim backdrop-blur-sm",
        fullScreenOnMobile ? "p-0 sm:p-4" : "p-4"
      )}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          "flex flex-col overflow-hidden border border-border bg-surface shadow-xl",
          fullScreenOnMobile
            ? "h-full max-h-full w-full rounded-none sm:h-auto sm:w-auto sm:max-h-[85vh] sm:rounded-2xl"
            : "max-h-[85vh] w-full rounded-2xl",
          defaultMaxWidth,
          className
        )}
      >
        {title && (
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-md p-1 text-muted transition-colors hover:bg-surface-muted hover:text-foreground"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-5">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-border bg-surface-muted/50 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
